import React from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import ChartCard from "@/components/dashboard/chart-card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { cn } from "@/lib/utils";

interface ChatLogsChartProps {
    chartContainerClassname?: string;
    title: string;
    value?: number;
    subtitle?: string;
    chartConfig: ChartConfig;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    chartData: any[];
    isLoading?: boolean;
}

const ChatLogsChart: React.FC<ChatLogsChartProps> = ({ chartContainerClassname, title, value, subtitle, chartConfig, chartData, isLoading = false }) => {
    if (isLoading) {
        return (
            <ChartCard title={title} value={value} subtitle={subtitle}>
                <div className="h-[507px] min-h-[200px] w-full flex items-center justify-center">
                    <p>Loading chart data...</p>
                </div>
            </ChartCard>
        );
    }

    return (
        <ChartCard title={title} value={value} subtitle={subtitle}>
            <ChartContainer config={chartConfig} className={cn("h-[507px] min-h-[200px] w-full", chartContainerClassname)}>
                <LineChart accessibilityLayer data={chartData}>
                    <CartesianGrid vertical={false} />
                    <XAxis 
                        dataKey="date" 
                        tickLine={false} 
                        tickMargin={10} 
                        axisLine={false} 
                        tickFormatter={(value) => {
                            // Format date to show as "Oct 11" or similar
                            // Handle different date formats that might come from API
                            let date;
                            if (typeof value === 'string') {
                                // Handle "YYYY-MM-DD" format specifically
                                if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                    // Convert YYYY-MM-DD to a valid date string for Date constructor
                                    // Replace hyphens with slashes as some browsers parse it better
                                    const dateStr = value.replace(/-/g, '/');
                                    date = new Date(dateStr);
                                } else {
                                    date = new Date(value);
                                }
                            } else {
                                // If it's already a Date object or can't be parsed, use it as is
                                date = new Date(value);
                            }
                            // Check if the date is valid before formatting
                            if (isNaN(date.getTime())) {
                                console.warn('Invalid date:', value);
                                return 'Invalid Date';
                            }
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }}
                    />
                    <YAxis />
                    <ChartTooltip 
                        content={<ChartTooltipContent 
                            formatter={(value) => [`${value}`, "Chats"]}
                            labelFormatter={(label) => {
                                // Handle different date formats that might come from API
                                let date;
                                if (typeof label === 'string') {
                                    // Handle "YYYY-MM-DD" format specifically
                                    if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                                        // Convert YYYY-MM-DD to a valid date string for Date constructor
                                        const dateStr = label.replace(/-/g, '/');
                                        date = new Date(dateStr);
                                    } else {
                                        date = new Date(label);
                                    }
                                } else {
                                    date = new Date(label);
                                }
                                // Check if the date is valid before formatting
                                if (isNaN(date.getTime())) {
                                    console.warn('Invalid date in tooltip:', label);
                                    return 'Invalid Date';
                                }
                                return date.toLocaleDateString('en-US', { 
                                    month: 'long', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                });
                            }}
                        />} 
                    />
                    <ChartLegend content={<ChartLegendContent />} />
                    {Object.keys(chartConfig).map((key) => {
                        return <Line 
                            key={key} 
                            dataKey={key} 
                            type={"monotone"} 
                            stroke={chartConfig[key].color} 
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 6 }} 
                        />;
                    })}
                </LineChart>
            </ChartContainer>
        </ChartCard>
    );
};

export default ChatLogsChart;